import type { Job, JobCallback } from "node-schedule";
import { RecurrenceRule } from "node-schedule";
import { TimeTrigger } from "../triggers/TimeTrigger";
import type { LoggingService } from "../types/LoggingService";
import type { Trigger } from "../types/Trigger";
import { TriggerScheduler } from "./TriggerScheduler";

/**
 * TimeTriggerScheduler
 */
export class TimeTriggerScheduler extends TriggerScheduler {
    private registered: [TimeTrigger, Job][] = [];

    /**
     *
     * @param scheduleJob Schedule
     * @param cancelJob Schedule
     * @param logger Log service
     */
    constructor(
        private scheduleJob: (rule: RecurrenceRule, callback: JobCallback) => Job,
        private cancelJob: (job: Job) => boolean,
        private logger: LoggingService,
    ) {
        super();
    }

    /**
     * @param trigger TimeTrigger
     */
    public register(trigger: TimeTrigger): void {
        this.logger.logDebug(`Register TimeTriggerScheduler trigger ${trigger}`);
        if (this.getAssociatedJob(trigger)) {
            this.loadregister();
            throw new Error(`TimeTriggerScheduler Trigger ${trigger} is already registered.`);
        } else {
            const newJob = this.scheduleJob(this.createRecurrenceRule(trigger), () => {
                this.logger.logDebug(`Executing TimeTriggerScheduler trigger ${trigger}`);
                const trigger_data = JSON.parse(JSON.stringify(trigger.getData()));
                trigger.getAction().execute(trigger_data);
            });
            this.registered.push([trigger, newJob]);
        }
    }

    /**
     * loadregister
     */
    public loadregister(): void {
        for (const r of this.registered) {
            this.logger.logDebug(`Check TimeTriggerScheduler ${r[0]}`);
        }
    }

    /**
     * @param trigger TimeTrigger
     */
    public unregister(trigger: TimeTrigger): void {
        this.logger.logDebug(`Unregister TimeTriggerScheduler trigger ${trigger}`);
        const job = this.getAssociatedJob(trigger);
        if (job) {
            this.cancelJob(job);
            this.removeTrigger(trigger);
        } else {
            this.loadregister();
            if (trigger.toString().toString().indexOf("TimeTriggerForAstroTrigger") !== -1) {
                this.logger.logDebug(`TimeTriggerScheduler trigger not found!`);
            } else {
                throw new Error(`TimeTriggerScheduler Trigger ${trigger} is not registered.`);
            }
        }
    }

    /**
     * destroy
     */
    public destroy(): void {
        this.registered.forEach(r => this.unregister(r[0]));
    }

    /**
     * forType
     *
     * @returns trigger action on/off
     */
    public forType(): string {
        return TimeTrigger.prototype.constructor.name;
    }

    /**
     * @param trigger OneTimeTrigger
     */
    private getAssociatedJob(trigger: TimeTrigger): Job | null {
        const entry = this.registered.find(r => r[0] === trigger);
        if (entry) {
            return entry[1];
        }
        this.loadregister();
        return null;
    }

    /**
     * @param trigger OneTimeTrigger
     */
    private removeTrigger(trigger: Trigger): void {
        this.registered = this.registered.filter(r => r[0] !== trigger);
    }

    /**
     * Create scheduler
     *
     * @param trigger OneTimeTrigger
     */
    private createRecurrenceRule(trigger: TimeTrigger): RecurrenceRule {
        const rule = new RecurrenceRule();
        rule.dayOfWeek = trigger.getWeekdays();
        rule.hour = trigger.getHour();
        rule.minute = trigger.getMinute();
        return rule;
    }
}
